import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { CardTreeNode } from '@/types'
import { formatLidForDisplay } from '@/utils/lid'
import './CardTree.css'

interface CardTreeProps {
  nodes: CardTreeNode[]
  level?: number
}

interface TreeNodeProps {
  node: CardTreeNode
  level: number
}

function TreeNode({ node, level }: TreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(level < 2)
  const hasChildren = node.children.length > 0

  return (
    <div className="tree-item">
      <div className="tree-item-header">
        {hasChildren ? (
          <button
            className={`tree-expand-btn ${isExpanded ? 'expanded' : ''}`}
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="9,18 15,12 9,6" />
            </svg>
          </button>
        ) : (
          <span className="tree-indent" />
        )}

        <Link to={`/card/${encodeURIComponent(node.card.lid)}`} className="tree-card-link">
          <span className="lid-badge">{formatLidForDisplay(node.card.lid)}</span>
          <span className="tree-card-title">{node.card.title}</span>
        </Link>
      </div>

      {hasChildren && isExpanded && (
        <div className="tree-item-children">
          {node.children.map((child) => (
            <TreeNode key={child.card.lid} node={child} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

export default function CardTree({ nodes, level = 0 }: CardTreeProps) {
  if (nodes.length === 0) {
    return null
  }

  return (
    <div className="card-tree">
      {nodes.map((node) => (
        <TreeNode key={node.card.lid} node={node} level={level} />
      ))}
    </div>
  )
}
